import type { ReactNode } from "react";

/**
 * The typographic vocabulary. Three components, and between them they are the
 * only place a font size is chosen.
 *
 * The hierarchy is carried by the gap between `MicroLabel` (11px, tracked out)
 * and `Metric` (up to 68px, tracked in). That jump is the design — it is what
 * makes a dense grey table read as structured rather than as a dump.
 */

/**
 * The Swiss caption: uppercase, widely tracked, muted. Every column head and
 * every field label in the application is one of these, and nothing else is.
 */
export function MicroLabel({
  children,
  as: Tag = "span",
  className = "",
}: {
  readonly children: ReactNode;
  readonly as?: "span" | "div" | "th" | "dt" | "legend";
  readonly className?: string;
}) {
  return (
    <Tag className={`text-micro font-medium uppercase text-ink-faint ${className}`}>{children}</Tag>
  );
}

export function SectionTitle({
  children,
  action,
}: {
  readonly children: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-rule pb-2">
      <h2 className="text-section font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

/**
 * A single number, set large. The unit sits beside it at body size rather than
 * being folded into the same string, so the digits keep a clean left edge when
 * several of these sit in a row — which is the whole reason to set them large.
 */
export function Metric({
  label,
  value,
  unit,
  tone = "ink",
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly tone?: "ink" | "signal" | "muted";
  readonly hint?: string;
}) {
  const toneClass =
    tone === "signal" ? "text-signal" : tone === "muted" ? "text-ink-faint" : "text-ink";

  /**
   * The display size is for digits. A metric occasionally has no figure to show —
   * "never", "now", an em dash — and setting a *word* at 68px makes it the loudest
   * thing on the page, which is backwards: the absence of a reading is the least
   * interesting cell in the row. Words drop to title size and let the numerals
   * beside them keep the emphasis.
   */
  const isFigure = /\d/.test(value);

  return (
    <div className="flex flex-col gap-1.5">
      {/*
        One line, always. The metrics in a masthead are top-aligned so their
        numerals share an edge, and that only holds while every label occupies the
        same single row — a two-line "On this page" beside a one-line "Online"
        would put the two figures at different heights again.
      */}
      <MicroLabel className="whitespace-nowrap">{label}</MicroLabel>
      {/*
        `before:content-['0']` is a baseline strut: a zero-width digit at display
        size, present in every metric whatever it renders.

        It fixes the row's height and its baseline, so a metric showing a word
        occupies exactly the same box as one showing a numeral — which is what keeps
        labels, figures and hints on shared lines across a masthead. And because
        everything here is baseline-aligned to that strut rather than to each other,
        the unit sits on the numeral's baseline instead of hanging off the bottom of
        its descender box.

        A pseudo-element rather than a real span, because a real one would join the
        text layer: it would prefix `innerText`, and an operator copying "4.3 kWh"
        out of the masthead to paste into a ticket would get "04.3 kWh".
      */}
      <div className="flex items-baseline font-display text-display before:inline-block before:w-0 before:overflow-hidden before:font-semibold before:content-['0']">
        <span className={`font-semibold ${isFigure ? "" : "text-title"} ${toneClass}`}>
          {value}
        </span>
        {unit === undefined ? null : (
          // `nowrap`: a two-word unit ("m ago") broke across two lines in a narrow
          // metric column and read as two separate captions.
          <span className="ml-1.5 whitespace-nowrap font-sans text-body font-medium text-ink-faint">
            {unit}
          </span>
        )}
      </div>
      {hint === undefined ? null : (
        <span className="text-micro text-ink-faint tracking-normal normal-case">{hint}</span>
      )}
    </div>
  );
}

/**
 * A label/value pair. Used wherever the alternative would be a definition list
 * with ad-hoc spacing, which is most detail views.
 */
export function Datum({
  label,
  children,
  mono = false,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 border-t border-rule pt-2">
      <MicroLabel as="dt">{label}</MicroLabel>
      <dd className={`m-0 text-data text-ink ${mono ? "break-all" : ""}`}>{children}</dd>
    </div>
  );
}
